import { useState, useCallback } from 'react'
import { useQuery, useMutation } from 'convex/react'
import { api } from '@convex/_generated/api'
import type { Id, Doc } from '@convex/_generated/dataModel'

// Re-export Convex types for convenience
export type Property = Doc<'properties'>
export type PropertyId = Id<'properties'>
export type BuildingId = Id<'buildings'>
export type PropertyDocumentId = Id<'property_documents'>

// Filter parameters for list query
export interface PropertyQueryParams {
  status?: 'draft' | 'published' | 'archived'
  featured?: boolean
  // Filter by building assignment: true = only with building, false = only standalone, undefined = all
  hasBuilding?: boolean
}

/**
 * Hook for fetching a list of properties with optional filters.
 * Uses Convex's real-time queries - data updates automatically when it changes.
 */
export function useProperties(params: PropertyQueryParams = {}) {
  const data = useQuery(api.properties.list, {
    status: params.status,
    featured: params.featured,
    hasBuilding: params.hasBuilding,
  })

  // Convex queries auto-refresh, but we provide refetch for API compatibility
  const refetch = useCallback(() => {
    // No-op: Convex queries are real-time and auto-update
  }, [])

  return {
    data,
    isLoading: data === undefined,
    error: null as Error | null, // Convex throws on error
    refetch,
  }
}

/**
 * Hook for fetching a single property by ID.
 * Uses Convex's real-time queries.
 */
export function useProperty(id: PropertyId | string | undefined) {
  const data = useQuery(
    api.properties.getById,
    id ? { id: id as PropertyId } : 'skip'
  )

  return {
    data,
    isLoading: data === undefined && id !== undefined,
    error: null as Error | null,
  }
}

/**
 * Hook for fetching a single property by slug.
 * Uses Convex's real-time queries.
 */
export function usePropertyBySlug(slug: string | undefined) {
  const data = useQuery(
    api.properties.getBySlug,
    slug ? { slug } : 'skip'
  )

  return {
    data,
    isLoading: data === undefined && slug !== undefined,
    error: null as Error | null,
  }
}

// Input types for mutations
export interface PropertyCreateInput {
  title: string
  slug: string
  // Descriptions
  description_short?: string
  description_long?: string
  // Price
  price?: number
  price_currency?: string
  // Location
  address?: string
  city?: string
  province?: string
  region?: string
  postal_code?: string
  // Property details
  bedrooms?: number
  bathrooms?: number
  area_m2?: number
  status: 'draft' | 'published' | 'archived'
  specs?: {
    bedrooms?: number
    bathrooms?: number
    area_m2?: number
    garage?: boolean // DEPRECATED: moved to characteristics
    garage_spaces?: number
    year_built?: number
    floor?: string // Floor level (e.g., "4", "baja", "ático")
  }
  // Characteristics
  characteristics?: {
    pool?: boolean
    garden?: boolean
    terrace?: boolean
    patio?: boolean
    air_conditioning?: boolean
    elevator?: boolean
    garage?: boolean
    storage_room?: boolean
    balcony?: boolean
    exterior?: boolean
    jacuzzi?: boolean
    sauna?: boolean
    bbq_area?: boolean
    sea_views?: boolean
    heating?: string
    orientation?: string
    views?: string
    energy_rating?: string
    furnished?: boolean
    storage?: boolean
    highlighted_features?: string[]
  }
  featured: boolean
  /** Customizable label/badge (e.g., "Segunda Mano | Oportunidad") */
  badge?: string
  sort_order?: number
  // Building relationship - for properties that belong to a development/promotion
  building_id?: BuildingId | string
  // Reference code for properties within a building (e.g., "VI-3-P02-E")
  reference_code?: string
  // Link to floor plan document
  floor_plan_id?: PropertyDocumentId | string
  // When true, property is ONLY shown in the building's Tipologias table
  building_only?: boolean
}

export type PropertyUpdateInput = Partial<PropertyCreateInput>

/**
 * Transform property input data for Convex mutation.
 * Casts string IDs to typed Convex IDs.
 */
function transformPropertyDataForMutation(data: PropertyCreateInput | PropertyUpdateInput) {
  const result = { ...data } as Record<string, unknown>

  // Cast building_id to typed ID if present
  if (data.building_id) {
    result.building_id = data.building_id as BuildingId
  }

  // Cast floor_plan_id to typed ID if present
  if (data.floor_plan_id) {
    result.floor_plan_id = data.floor_plan_id as PropertyDocumentId
  }

  return result
}

/**
 * Hook for creating a new property.
 * Returns a mutation function that can be called with property data.
 */
export function useCreateProperty() {
  const createMutation = useMutation(api.properties.create)
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const mutateAsync = useCallback(async (data: PropertyCreateInput) => {
    setIsPending(true)
    setError(null)
    try {
      const transformedData = transformPropertyDataForMutation(data)
      const result = await createMutation({
        data: transformedData as Parameters<typeof createMutation>[0]['data'],
        ip_address: undefined,
        user_agent: navigator.userAgent,
      })
      return result
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)))
      throw err
    } finally {
      setIsPending(false)
    }
  }, [createMutation])

  return {
    mutate: mutateAsync,
    mutateAsync,
    isPending,
    error,
  }
}

/**
 * Hook for updating an existing property.
 * Returns a mutation function that can be called with property ID and update data.
 */
export function useUpdateProperty() {
  const updateMutation = useMutation(api.properties.update)
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const mutateAsync = useCallback(async ({ id, data }: { id: PropertyId | string; data: PropertyUpdateInput }) => {
    setIsPending(true)
    setError(null)
    try {
      const transformedData = transformPropertyDataForMutation(data)
      const result = await updateMutation({
        id: id as PropertyId,
        data: transformedData as Parameters<typeof updateMutation>[0]['data'],
        ip_address: undefined,
        user_agent: navigator.userAgent,
      })
      return result
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)))
      throw err
    } finally {
      setIsPending(false)
    }
  }, [updateMutation])

  return {
    mutate: mutateAsync,
    mutateAsync,
    isPending,
    error,
  }
}

/**
 * Hook for deleting a property.
 * Returns a mutation function that can be called with property ID.
 */
export function useDeleteProperty() {
  const deleteMutation = useMutation(api.properties.remove)
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const mutateAsync = useCallback(async (id: PropertyId | string) => {
    setIsPending(true)
    setError(null)
    try {
      const result = await deleteMutation({
        id: id as PropertyId,
        ip_address: undefined,
        user_agent: navigator.userAgent,
      })
      return result
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)))
      throw err
    } finally {
      setIsPending(false)
    }
  }, [deleteMutation])

  return {
    mutate: mutateAsync,
    mutateAsync,
    isPending,
    error,
  }
}

// Reorder input type
export interface PropertyReorderItem {
  id: PropertyId | string
  sort_order: number
}

/**
 * Hook for reordering properties.
 * Returns a mutation function that can be called with an array of {id, sort_order} items.
 */
export function useReorderProperties() {
  const reorderMutation = useMutation(api.properties.reorder)
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const mutateAsync = useCallback(async (items: PropertyReorderItem[]) => {
    setIsPending(true)
    setError(null)
    try {
      const result = await reorderMutation({
        items: items.map(item => ({
          id: item.id as PropertyId,
          sort_order: item.sort_order,
        })),
        ip_address: undefined,
        user_agent: navigator.userAgent,
      })
      return result
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)))
      throw err
    } finally {
      setIsPending(false)
    }
  }, [reorderMutation])

  return {
    mutate: mutateAsync,
    mutateAsync,
    isPending,
    error,
  }
}

// Parameters for listByBuilding query
export interface PropertiesByBuildingParams {
  buildingId: Id<'buildings'>
  status?: 'draft' | 'published' | 'archived'
}

/**
 * Hook for fetching properties belonging to a specific building.
 * Used for the Tipologias table display on building pages.
 * Returns properties with all fields needed for the table:
 * reference_code, bedrooms, bathrooms, price, area_m2, garage (from specs),
 * storage_room, terrace_m2, floor_plan_id
 */
export function usePropertiesByBuilding(params: PropertiesByBuildingParams | null) {
  const data = useQuery(
    api.properties.listByBuilding,
    params ? { building_id: params.buildingId, status: params.status } : 'skip'
  )

  const refetch = useCallback(() => {
    // No-op: Convex queries are real-time and auto-update
  }, [])

  return {
    data,
    isLoading: data === undefined && params !== null,
    error: null as Error | null,
    refetch,
  }
}
